import * as AccessibleIcon from '@radix-ui/react-accessible-icon';
import cx from 'classnames';
import React from 'react';
import { Icons, IconType } from '@mutiny-pkg/dumpster-ui/icons';
import { Badge } from '../Badge/Badge';
import { Tooltip, TooltipProps } from '../Tooltip';
import * as style from './Button.css';
import { NoticeDot } from './NoticeDot';
import { Spinner } from '../Loaders/Spinner';

export enum ButtonVariantEnum {
  primary = 'primary',
  primaryDark = 'primaryDark',
  secondary = 'secondary',
  secondaryLight = 'secondaryLight',
  tertiary = 'tertiary',
  destructive = 'destructive',
  recommendation = 'recommendation',
  text = 'text',
  close = 'close',
  modalClose = 'modalClose',
  aiPrimary = 'aiPrimary',
  aiSecondary = 'aiSecondary',
  filter = 'filter',
  transparent = 'transparent',
}

export enum ButtonSizeEnum {
  small = 'small',
  large = 'large',
  minimal = 'minimal',
}

export type ButtonVariantTypes = NonNullable<style.ButtonVariants>;

type BaseButtonProps = style.ButtonVariants &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    tooltipContent?: TooltipProps['content'];
    tooltipProps?: Partial<TooltipProps>;
  };

export type ButtonProps = BaseButtonProps & {
  iconLeft?: IconType;
  iconRight?: IconType;
  loading?: boolean;
  iconSize?: style.IconSizeVariant;
};

export type IconButtonProps = Omit<BaseButtonProps, 'iconOnly'> & {
  icon: IconType;
  iconSize?: style.IconSizeVariant;
  label: string;
  children?: never;
};

export type BadgeButtonProps = Omit<BaseButtonProps, 'variant' | 'iconOnly'> &
  Pick<ButtonProps, 'iconLeft'> & {
    variant: 'secondary' | 'tertiary' | 'highlight';
    badgeText: string | number;
    hideBadge?: boolean;
  };

export type NoticeButtonProps = ButtonProps & {
  notice: string | null;
};

const ButtonInnerContentWrapper = ({ loading, children }: Partial<ButtonProps>) =>
  loading ? <div className={style.loadingButtonContent}>{children}</div> : <>{children}</>;

const InnerButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      fullWidth,
      display,
      iconOnly = false,
      disabled = false,
      children,
      iconLeft: IconLeft,
      iconRight: IconRight,
      loading = false,
      className,
      type,
      iconSize = 'default',
      ...props
    }: ButtonProps,
    forwardedRef,
  ): JSX.Element => {
    return (
      <button
        type={type ?? 'button'}
        className={cx(
          style.button({
            iconOnly,
            variant,
            size,
            fullWidth,
            display,
            iconSize,
          }),
          className,
        )}
        disabled={disabled || loading}
        ref={forwardedRef}
        {...props}
      >
        <ButtonInnerContentWrapper loading={loading}>
          {variant === ButtonVariantEnum.recommendation && (
            <Icons.Sparkles2 className={style.buttonIcon({ size: 'default' })} />
          )}
          {IconLeft && <IconLeft className={style.buttonIcon({ size: 'default' })} />}
          {children}
          {IconRight && <IconRight className={style.buttonIcon({ size: 'default' })} />}
        </ButtonInnerContentWrapper>
        {loading && <Spinner className={style.loadingIconPlacement} />}
      </button>
    );
  },
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ tooltipContent, tooltipProps, ...props }: ButtonProps, forwardRef) => {
    if (tooltipContent) {
      return (
        <Tooltip asChild content={tooltipContent} {...tooltipProps}>
          <InnerButton {...props} ref={forwardRef} />
        </Tooltip>
      );
    }

    return <InnerButton {...props} ref={forwardRef} />;
  },
);

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon: Icon, label, iconSize = 'default', ...props }: IconButtonProps, forwardedRef): JSX.Element => {
    return (
      <Button {...props} ref={forwardedRef} iconOnly iconSize={iconSize}>
        <AccessibleIcon.Root label={label}>
          <Icon className={style.iconSizes[iconSize]} />
        </AccessibleIcon.Root>
      </Button>
    );
  },
);

export const BadgeButton = React.forwardRef<HTMLButtonElement, BadgeButtonProps>(
  ({ badgeText, children, iconLeft, hideBadge, ...props }: BadgeButtonProps, forwardedRef): JSX.Element => (
    <Button {...props} ref={forwardedRef} iconLeft={iconLeft}>
      {children}
      {hideBadge ? null : <Badge variant="badgedButton">{badgeText}</Badge>}
    </Button>
  ),
);

export const NoticeButton = React.forwardRef<HTMLButtonElement, NoticeButtonProps>(
  ({ notice, children, tooltipProps, ...props }: NoticeButtonProps, forwardedRef): JSX.Element => {
    if (notice == null) {
      return (
        <Button {...props} ref={forwardedRef}>
          {children}
        </Button>
      );
    }

    const { variant } = props;

    return (
      <Tooltip asChild content={notice} {...tooltipProps}>
        <Button {...props} ref={forwardedRef}>
          {children}
          <NoticeDot
            border={
              !variant || variant === 'primary' || variant === 'primaryDark' || variant === 'recommendation'
                ? 'light'
                : 'dark'
            }
          />
        </Button>
      </Tooltip>
    );
  },
);
